import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
  Chip,
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RoomRoundedIcon from "@mui/icons-material/RoomRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { formatMeters, formatSignedMeters, formatTimeOnly } from "../../utils/fdcHelpers";
import {
  tacticalAccordionSx,
  tacticalSummarySx,
  tacticalChipSx,
  tableHeadCellSx,
  tableBodyCellSx,
} from "../../utils/fdcTheme";

export default function ProcessedObservationsPanel({
  expanded,
  onChange,
  hasMission,
  processedObservations,
  selectedProcessedObservationId,
  setSelectedProcessedObservationId,
  onDelete,
}) {
  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      disabled={!hasMission}
      disableGutters
      sx={(theme) => tacticalAccordionSx(theme)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={(theme) => tacticalSummarySx(theme)}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
            Processed Observations
          </Typography>
          <Chip
            size="small"
            label={`${processedObservations.length} obs`}
            sx={(theme) => tacticalChipSx(theme, processedObservations.length ? "success" : "neutral")}
          />
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        {!processedObservations.length ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="info" sx={{ fontWeight: 600 }}>
              No processed observations yet.
            </Alert>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Table size="small" sx={{ "& td, & th": { borderBottom: 0 }, minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx}>Obs</TableCell>
                <TableCell sx={tableHeadCellSx}>Time</TableCell>
                <TableCell sx={tableHeadCellSx} align="right">ΔE</TableCell>
                <TableCell sx={tableHeadCellSx} align="right">ΔN</TableCell>
                <TableCell sx={tableHeadCellSx} align="right">Distance</TableCell>
                <TableCell sx={tableHeadCellSx} align="right">Altitude</TableCell>
                <TableCell sx={(t) => ({ ...tableHeadCellSx(t), width: 84 })}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processedObservations.map((obs) => {
                const metrics = obs.displayMetrics || {};
                const observationId = String(
                  obs.id ?? obs.observation_id ?? obs.processed_observation_id ?? obs.shot_id,
                );
                const isSelected = String(selectedProcessedObservationId ?? "") === observationId;

                const handleSelect = () => {
                  if (typeof setSelectedProcessedObservationId !== "function") return;
                  setSelectedProcessedObservationId((prev) =>
                    String(prev ?? "") === observationId ? null : observationId,
                  );
                };

                return (
                  <TableRow
                    key={obs.id}
                    hover
                    onClick={handleSelect}
                    selected={isSelected}
                    sx={(t) => ({
                      cursor: "pointer",
                      "&.Mui-selected": {
                        bgcolor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.1 : 0.06),
                      },
                      "&.Mui-selected:hover": {
                        bgcolor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.14 : 0.09),
                      },
                    })}
                  >
                    <TableCell sx={(t) => ({ ...tableBodyCellSx(t), fontWeight: 700 })}>
                      #{obs.shot_id}
                    </TableCell>
                    <TableCell sx={(t) => ({ ...tableBodyCellSx(t), color: t.palette.text.secondary })}>
                      {formatTimeOnly(obs.created_at)}
                    </TableCell>
                    <TableCell sx={tableBodyCellSx} align="right">
                      {formatSignedMeters(metrics.deltaE)}
                    </TableCell>
                    <TableCell sx={tableBodyCellSx} align="right">
                      {formatSignedMeters(metrics.deltaN)}
                    </TableCell>
                    <TableCell sx={tableBodyCellSx} align="right">
                      {formatMeters(metrics.distance)}
                    </TableCell>
                    <TableCell sx={tableBodyCellSx} align="right">
                      {formatMeters(obs.observed_z)}
                    </TableCell>
                    <TableCell sx={(t) => ({ ...tableBodyCellSx(t), p: 0.5 })} onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.25}>
                        <Tooltip title={isSelected ? "Focused on map" : "Focus on map"} arrow>
                          <IconButton
                            size="small"
                            onClick={handleSelect}
                            sx={{ color: isSelected ? "warning.main" : "text.secondary" }}
                          >
                            <RoomRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {onDelete && (
                          <Tooltip title="Delete observation" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); onDelete(obs.id, obs.shot_id); }}
                              sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                            >
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
